import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CompetencyFramework = () => {
  const [activeFramework, setActiveFramework] = useState('technical');

  const competencyFrameworks = {
    technical: {
      title: 'Compétences Techniques',
      icon: 'Settings',
      color: 'blue',
      description: 'Maîtrise des technologies et outils numériques essentiels',
      levels: [
        {
          level: 'Fondamental',
          skills: ['Utilisation d\'outils numériques de base', 'Compréhension des concepts IA', 'Navigation dans environnements digitaux'],
          duration: '2-4 semaines',
          certification: 'Certificat Fondamental Digital'
        },
        {
          level: 'Intermédiaire',
          skills: ['Implémentation solutions IA simples', 'Analyse de données basique', 'Gestion projets numériques'],
          duration: '8-12 semaines',
          certification: 'Certificat Praticien Digital'
        },
        {
          level: 'Avancé',
          skills: ['Développement solutions IA complexes', 'Architecture systèmes intelligents', 'Leadership technique'],
          duration: '16-24 semaines',
          certification: 'Certificat Expert IA'
        },
        {
          level: 'Expert',
          skills: ['Innovation technologique', 'Recherche & Développement IA', 'Mentoring technique avancé'],
          duration: 'Formation continue',
          certification: 'Master Certification IA'
        }
      ]
    },
    leadership: {
      title: 'Leadership Numérique',
      icon: 'Users',
      color: 'green',
      description: 'Capacités de direction et de management dans l\'ère digitale',
      levels: [
        {
          level: 'Émergent',
          skills: ['Communication digitale efficace', 'Gestion équipes hybrides', 'Sensibilisation changement'],
          duration: '3-6 semaines',
          certification: 'Certificat Leader Émergent'
        },
        {
          level: 'Confirmé',
          skills: ['Stratégie transformation digitale', 'Management du changement', 'Vision innovation'],
          duration: '10-14 semaines',
          certification: 'Certificat Manager Digital'
        },
        {
          level: 'Senior',
          skills: ['Leadership stratégique IA', 'Culture innovation organisationnelle', 'Écosystème partenaires'],
          duration: '18-26 semaines',
          certification: 'Certificat Directeur Transformation'
        },
        {
          level: 'Visionnaire',
          skills: ['Prospective technologique', 'Influence sectorielle', 'Révolution organisationnelle'],
          duration: 'Développement continu',
          certification: 'Executive Certificate Innovation'
        }
      ]
    },
    adaptability: {
      title: 'Adaptabilité & Innovation',
      icon: 'RefreshCw',
      color: 'purple',
      description: 'Capacité d\'adaptation et d\'innovation continue',
      levels: [
        {
          level: 'Réactif',
          skills: ['Veille technologique active', 'Apprentissage rapide nouvelles technologies', 'Flexibilité opérationnelle'],
          duration: '4-6 semaines',
          certification: 'Certificat Adaptabilité Digitale'
        },
        {
          level: 'Proactif',
          skills: ['Anticipation tendances', 'Expérimentation contrôlée', 'Amélioration continue'],
          duration: '12-16 semaines',
          certification: 'Certificat Innovateur Méthodique'
        },
        {
          level: 'Transformateur',
          skills: ['Disruption créative', 'Innovation systémique', 'Révolution processus'],
          duration: '20-28 semaines',
          certification: 'Certificat Agent Transformation'
        },
        {
          level: 'Pionnier',
          skills: ['Création nouveaux paradigmes', 'Influence écosystème', 'Leadership d\'opinion'],
          duration: 'Évolution permanente',
          certification: 'Master Innovation Disruptive'
        }
      ]
    }
  };

  const frameworks = Object.keys(competencyFrameworks);

  return (
    <section id="competency-framework" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Référentiel de Compétences Structuré</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Notre framework de développement des compétences s'articule autour de trois dimensions essentielles, 
            chacune déclinée en quatre niveaux de maîtrise progressifs.
          </p>
        </div>

        {/* Framework Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {frameworks?.map((key) => (
            <button
              key={key}
              onClick={() => setActiveFramework(key)}
              className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                activeFramework === key
                  ? `bg-${competencyFrameworks?.[key]?.color}-600 text-white shadow-lg transform scale-105`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon name={competencyFrameworks?.[key]?.icon} size={24} />
              <span>{competencyFrameworks?.[key]?.title}</span>
            </button>
          ))}
        </div>

        {/* Active Framework Content */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center justify-center w-20 h-20 bg-${competencyFrameworks?.[activeFramework]?.color}-100 rounded-2xl mb-6`}>
              <Icon name={competencyFrameworks?.[activeFramework]?.icon} size={40} className={`text-${competencyFrameworks?.[activeFramework]?.color}-600`} />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">{competencyFrameworks?.[activeFramework]?.title}</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{competencyFrameworks?.[activeFramework]?.description}</p>
          </div>

          {/* Competency Levels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {competencyFrameworks?.[activeFramework]?.levels?.map((level, index) => (
              <div
                key={level?.level}
                className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-gray-200 hover:border-blue-500 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 bg-${competencyFrameworks?.[activeFramework]?.color}-100 rounded-lg flex items-center justify-center`}>
                      <span className={`text-xl font-bold text-${competencyFrameworks?.[activeFramework]?.color}-600`}>
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{level?.level}</h4>
                      <p className="text-sm text-gray-600">{level?.duration}</p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 bg-${competencyFrameworks?.[activeFramework]?.color}-100 text-${competencyFrameworks?.[activeFramework]?.color}-700 rounded-full text-xs font-semibold`}>
                    Niveau {index + 1}
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Skills */}
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-3">Compétences Développées</h5>
                    <ul className="space-y-2">
                      {level?.skills?.map((skill, skillIndex) => (
                        <li key={skillIndex} className="flex items-start space-x-3">
                          <Icon name="CheckCircle" size={16} className={`text-${competencyFrameworks?.[activeFramework]?.color}-600 mt-1`} />
                          <span className="text-gray-700 text-sm">{skill}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Certification */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <Icon name="Award" size={20} className={`text-${competencyFrameworks?.[activeFramework]?.color}-600`} />
                      <div>
                        <h6 className="font-semibold text-gray-900">Certification</h6>
                        <p className="text-sm text-gray-600">{level?.certification}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Framework Statistics */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
            <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">Impact du Framework</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">95%</div>
                <div className="text-sm text-gray-600">Taux de Réussite Certification</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">2.8x</div>
                <div className="text-sm text-gray-600">Amélioration Performance</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">87%</div>
                <div className="text-sm text-gray-600">Satisfaction Apprenants</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">3.2M€</div>
                <div className="text-sm text-gray-600">Économies Générées</div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-4">Commencez Votre Parcours de Développement</h4>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Identifiez votre niveau actuel et construisez un plan de développement personnalisé 
              basé sur notre référentiel de compétences éprouvé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className={`bg-${competencyFrameworks?.[activeFramework]?.color}-600 hover:bg-${competencyFrameworks?.[activeFramework]?.color}-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 shadow-lg`}>
                Évaluation Personnalisée
              </button>
              <button className="border-2 border-gray-300 text-gray-700 hover:border-gray-400 px-8 py-4 rounded-lg font-semibold transition-colors duration-200">
                Télécharger le Framework
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompetencyFramework;